import styled from '@emotion/styled'
import React, { ImgHTMLAttributes } from 'react'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useImageGalleryContext } from '@f-fertighaus/applications/Next/components/common/ImageGallery/ImageGalleryContext'
import { MobileOnly } from '@f-fertighaus/applications/Next/components/common/MediaQuery'
import { IMAGE_ASPECT_RATIO } from '@f-fertighaus/applications/Next/config/constants'
import { ImageSource } from '@f-fertighaus/applications/Next/types/houseDetailsResponse'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'

import { ImagesTab } from '../HouseImageGallery/HouseImageGallery'
import { FavoriteButton } from './components/FavoriteButton'
import { FloorPlansButton } from './components/FloorPlansButton'
import { ImageContainer } from './components/ImageContainer.styled'

import 'swiper/swiper-bundle.css'

export interface ClickableImageSource extends ImageSource {
  handleOpenGallery: () => void
  isPlaceholder?: boolean
}

const SWIPER_PERCENT_WIDTH = 100

function hasOnlyOneElement<T>(arr: T[]): arr is [T] {
  return arr.length === 1
}

export function useOpenImageGallery() {
  const { dispatch } = useImageGalleryContext()

  const onExteriorImageClick = () =>
    dispatch({ type: 'openGallery', initialTabIndex: ImagesTab.houseImages })
  const onFloorPlanImageClick = () =>
    dispatch({ type: 'openGallery', initialTabIndex: ImagesTab.floorPlans })

  return {
    onExteriorImageClick,
    onFloorPlanImageClick
  }
}

const trackExpandImage = (imgUrl: string) =>
  webtrack({
    category: 'image',
    action: 'click',
    label: 'expand-image',
    location: 'image-carousel',
    values: [{ type: 'url', value: imgUrl }]
  })

export const HouseImageCarousel = () => {
  const {
    state: { floor_plans, exterior_images, interior_images, active },
    featureFlags
  } = useHouseDetail()

  const house_images = [...exterior_images, ...interior_images]

  const { onFloorPlanImageClick, onExteriorImageClick } = useOpenImageGallery()

  const images = React.useMemo<ClickableImageSource[]>(
    () =>
      floor_plans
        ? [
            ...house_images.map<ClickableImageSource>(i => ({
              ...i,
              handleOpenGallery: onExteriorImageClick
            })),
            ...floor_plans.map<ClickableImageSource>(p => ({
              ...p,
              handleOpenGallery: onFloorPlanImageClick
            }))
          ]
        : house_images.map<ClickableImageSource>(i => ({
            ...i,
            handleOpenGallery: onExteriorImageClick
          })),
    [house_images, floor_plans]
  )
  const computedSwiperHeight = SWIPER_PERCENT_WIDTH / IMAGE_ASPECT_RATIO

  const showAddToFavourite = !featureFlags.includes(
    '@FF_DISABLE_ADD_TO_FAVOURITE'
  )

  return (
    <MobileOnly>
      <ImageContainer>
        {hasOnlyOneElement(images) ? (
          <img
            alt={images[0].alt_text}
            style={{ width: '100%', height: '100%' }}
            srcSet={`${images[0]['fill-320x240']} 450w,
                     ${images[0]['width-640x480']} 600w,
                     ${images[0]['width-1280x960']} 1000w,
                     ${images[0]['max-1600x1200.watermarked']} 1600w`}
            sizes='100vw'
            src={`${images[0]['width-640x480']}`}
            onClick={(event: React.MouseEvent<HTMLImageElement>) => {
              trackExpandImage(event.currentTarget.src)
              images[0].handleOpenGallery()
            }}
          />
        ) : (
          <Swiper
            className='hdp-swiper hdp-image-carousel'
            pagination={{
              clickable: false,
              type: 'bullets',
              dynamicBullets: true,
              dynamicMainBullets: 3,
              renderBullet: function (index, className) {
                return '<span class="' + className + '"></span>'
              }
            }}
            modules={[Pagination]}
            slidesPerView={1}
            spaceBetween={0}
            style={{
              height: `${computedSwiperHeight}vw`
            }}>
            {images.map((image, index) => (
              <SwiperSlide
                key={image['max-1600x1200.watermarked']}
                style={{ position: 'relative' }}>
                <ImageLazy
                  image={image}
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
                {index !== 0 && <Preloader className='swiper-lazy-preloader' />}
              </SwiperSlide>
            ))}
          </Swiper>
        )}
        {showAddToFavourite && (
          <FavouriteButtonContainer>
            {active && <FavoriteButton />}
          </FavouriteButtonContainer>
        )}
        {Boolean(floor_plans) && <FloorPlansButton />}
      </ImageContainer>
    </MobileOnly>
  )
}

const FavouriteButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: 16px;
`

const Preloader = styled.div``

const ImageLazy = ({
  image,
  loading
}: {
  image: ClickableImageSource
  loading: ImgHTMLAttributes<HTMLImageElement>['loading']
}) => (
  <img
    key={image['max-1600x1200.watermarked']}
    className='swiper-lazy'
    loading={loading}
    alt={image.alt_text}
    style={{
      width: '100%',
      height: '100%'
    }}
    src={image['width-640x480']}
    data-src={`${image['fill-320x240']}`}
    data-srcset={`${image['fill-320x240']} 450w,
                  ${image['width-640x480']} 640w,
                  ${image['width-1280x960']} 1280w,
                  ${image['max-1600x1200.watermarked']} 1600w`}
    sizes='100vw'
    onClick={(event: React.MouseEvent<HTMLImageElement>) => {
      trackExpandImage(event.currentTarget.src)
      image.handleOpenGallery()
    }}
  />
)
